import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Avatar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  getMedia,
  createCustomPinIcon,
  updateCustomPinIcon,
  deleteCustomPinIcon,
  createMapBackgroundImage,
  updateMapBackgroundImage,
  deleteMapBackgroundImage,
} from '../services/api';
import GlobalContext, {
  useOrganizationChange,
} from '../contexts/GlobalContext';
import MediaUploadModal from './MediaUploadModal';

const CustomToolbar = ({ onAddMapImage, onAddIconImage }) => {
  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<ImageIcon />}
        onClick={onAddMapImage}
        variant="contained"
        sx={{ mr: 1 }}
      >
        Upload Map Image
      </Button>
      <Button
        color="secondary"
        startIcon={<AddIcon />}
        onClick={onAddIconImage}
        variant="contained"
        sx={{ mr: 1 }}
      >
        Upload Icon Image
      </Button>
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
};

const MediaManager = ({ organizations = [] }) => {
  const { selectedOrganization } = useContext(GlobalContext);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [mediaUploadModalOpen, setMediaUploadModalOpen] = useState(false);
  const [currentUploadType, setCurrentUploadType] = useState('icon'); // 'icon' or 'map'
  const [editMode, setEditMode] = useState(false); // Whether we're editing (true) or creating (false)
  const [editingItem, setEditingItem] = useState(null); // Item being edited

  useEffect(() => {
    loadMediaItems();
  }, [selectedOrganization]);

  // Reload media items when organization changes
  useOrganizationChange(() => {
    loadMediaItems();
  }, []);

  const loadMediaItems = async () => {
    setLoading(true);
    try {
      const response = await getMedia();
      setMediaItems(response?.results || []);
    } catch (error) {
      console.error('Error loading media items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMapImage = () => {
    setCurrentUploadType('map');
    setEditMode(false);
    setEditingItem(null);
    setMediaUploadModalOpen(true);
  };

  const handleAddIconImage = () => {
    setCurrentUploadType('icon');
    setEditMode(false);
    setEditingItem(null);
    setMediaUploadModalOpen(true);
  };

  const handleMediaUploadSuccess = newMedia => {
    // Reload media items to show the new/updated media
    loadMediaItems();
    setMediaUploadModalOpen(false);
    setEditMode(false);
    setEditingItem(null);
  };

  const handleEdit = item => {
    // Allow editing both custom pin icons and map backgrounds
    if (
      item.media_type !== 'custom_pin_icon' &&
      item.media_type !== 'map_background'
    ) {
      return;
    }

    // Set up edit mode
    setEditMode(true);
    setEditingItem({
      id: item.related_object_id,
      related_object_id: item.related_object_id,
      name: item.name,
      description: item.description || '',
      vendor: item.vendor || null,
      entity_model_id: item.entity_model_id || null,
    });
    setCurrentUploadType(
      item.media_type === 'custom_pin_icon' ? 'icon' : 'map'
    );
    setMediaUploadModalOpen(true);
  };

  const handleDelete = item => {
    // Allow deleting both custom pin icons and map backgrounds
    if (
      item.media_type !== 'custom_pin_icon' &&
      item.media_type !== 'map_background'
    ) {
      return;
    }

    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handlePreview = item => {
    setPreviewItem(item);
    setPreviewDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.media_type === 'map_background') {
        await deleteMapBackgroundImage(itemToDelete.related_object_id);
      } else {
        await deleteCustomPinIcon(itemToDelete.related_object_id);
      }

      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadMediaItems();
    } catch (error) {
      console.error('Error deleting media item:', error);
    }
  };

  const columns = [
    {
      field: 'media_preview',
      headerName: 'Preview',
      width: 80,
      sortable: false,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Avatar
          src={params.row.file_url}
          alt={params.row.name}
          sx={{ width: 32, height: 32 }}
          variant="rounded"
        >
          <ImageIcon />
        </Avatar>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'media_type',
      headerName: 'Type',
      width: 140,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Chip
          label={
            params.value === 'map_background' ? 'Map Background' : 'Custom Icon'
          }
          size="small"
          color={params.value === 'map_background' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 200,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {params.value || 'No description'}
        </Typography>
      ),
    },
    {
      field: 'vendor_name',
      headerName: 'Vendor',
      width: 130,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => {
        // Only show vendor for custom icons
        if (params.row.media_type === 'custom_pin_icon') {
          return params.value ? (
            <Chip label={params.value} size="small" color="info" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return null;
      },
    },
    {
      field: 'entity_model_name',
      headerName: 'Entity model',
      width: 160,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => {
        if (params.row.media_type === 'custom_pin_icon') {
          return params.value ? (
            <Chip label={params.value} size="small" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return null;
      },
    },
    {
      field: 'organization_name',
      headerName: 'Organization',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Chip
          label={params.row.organization?.name || 'Unknown'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {params.row.created_by?.username || 'Unknown'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 130,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      headerAlign: 'left',
      align: 'left',
      getActions: params => {
        const actions = [
          <GridActionsCellItem
            icon={
              <Tooltip title="Preview">
                <VisibilityIcon />
              </Tooltip>
            }
            label="Preview"
            onClick={() => handlePreview(params.row)}
          />,
        ];

        // Show edit and delete for both custom pin icons and map backgrounds
        if (
          params.row.media_type === 'custom_pin_icon' ||
          params.row.media_type === 'map_background'
        ) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Edit">
                  <EditIcon />
                </Tooltip>
              }
              label="Edit"
              onClick={() => handleEdit(params.row)}
            />,
            <GridActionsCellItem
              icon={
                <Tooltip title="Delete">
                  <DeleteIcon />
                </Tooltip>
              }
              label="Delete"
              onClick={() => handleDelete(params.row)}
              showInMenu
            />
          );
        }

        return actions;
      },
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, textAlign: 'left' }}>
        Media Management
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3, textAlign: 'left' }}
      >
        Manage custom pin icons and media files for your organization. Upload
        new images, edit existing ones, or remove unused media.
      </Typography>

      {/* Upload Buttons Toolbar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button
          color="primary"
          startIcon={<ImageIcon />}
          onClick={handleAddMapImage}
          variant="contained"
        >
          Upload Map Image
        </Button>
        <Button
          color="secondary"
          startIcon={<AddIcon />}
          onClick={handleAddIconImage}
          variant="contained"
        >
          Upload Icon Image
        </Button>
      </Box>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={mediaItems}
          columns={columns}
          loading={loading}
          pageSizeOptions={[5, 10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{itemToDelete?.name}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>{previewItem?.name}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 2,
            }}
          >
            <Avatar
              src={previewItem?.icon_image_url}
              alt={previewItem?.name}
              sx={{ width: 128, height: 128 }}
              variant="rounded"
            >
              <ImageIcon sx={{ fontSize: 64 }} />
            </Avatar>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6">{previewItem?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {previewItem?.description || 'No description'}
              </Typography>
              <Chip
                label={previewItem?.organization?.name || 'Unknown'}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Media Upload/Edit Modal */}
      <MediaUploadModal
        open={mediaUploadModalOpen}
        onClose={() => {
          setMediaUploadModalOpen(false);
          setEditMode(false);
          setEditingItem(null);
        }}
        onUploadSuccess={handleMediaUploadSuccess}
        selectedOrganization={selectedOrganization?.id}
        mediaType={currentUploadType}
        uploadFunction={
          currentUploadType === 'icon'
            ? createCustomPinIcon
            : createMapBackgroundImage
        }
        updateFunction={
          currentUploadType === 'icon'
            ? updateCustomPinIcon
            : updateMapBackgroundImage
        }
        fileFieldName={
          currentUploadType === 'icon' ? 'icon_image' : 'image_file'
        }
        editMode={editMode}
        editData={editingItem}
      />
    </Box>
  );
};

export default MediaManager;
